import { DataTable, EmptyState, LevelMeter, type DataTableColumn } from '@wundo/ui';
import type { TrackedPageRoute } from '@wundo/shared/pageVisit.ts';
import type { PageUsageRow } from '@/lib/dashboard.ts';
import { ChartCard } from './ChartCard.tsx';
import { ONBOARDING_STEPS, TRACKED_PAGES } from './trackedPages.ts';

interface StepRow {
  route: TrackedPageRoute;
  label: string;
  users: number;
}

function columns(started: number): DataTableColumn<StepRow>[] {
  return [
    { key: 'label', header: 'Step' },
    {
      key: 'users',
      header: 'Reached',
      nowrap: true,
      render: (row) => (
        <div className='flex items-center gap-3'>
          <LevelMeter
            value={started === 0 ? 0 : row.users / started}
            label={`${row.label} reached`}
            className='w-32'
          />
          {started === 0
            ? '–'
            : `${Math.round((row.users / started) * 100)}% · ${row.users}`}
        </div>
      ),
    },
  ];
}

export interface OnboardingStepsCardProps {
  pages: PageUsageRow[];
}

/**
 * How far newcomers got through onboarding, against everybody who saw the
 * welcome step. Not a strict funnel: each newcomer takes one branch of these
 * steps, so a dip is not always a drop-off.
 */
export function OnboardingStepsCard({ pages }: OnboardingStepsCardProps) {
  const users = new Map(pages.map((page) => [page.route, page.users]));
  const rows: StepRow[] = ONBOARDING_STEPS.map((route) => ({
    route,
    label: TRACKED_PAGES[route].label,
    users: users.get(route) ?? 0,
  }));
  const started = rows[0]?.users ?? 0;

  return (
    <ChartCard
      title='Onboarding'
      description='Share of the people who saw Welcome. The steps branch: pilots see How it works and Ready to practise; everyone else sees What you get, Calendar, and Colleagues or First meeting.'
    >
      <DataTable<StepRow>
        label='Onboarding steps'
        columns={columns(started)}
        rows={started === 0 ? [] : rows}
        rowKey={(row) => row.route}
        emptyState={<EmptyState title='Nobody went through onboarding yet' />}
      />
    </ChartCard>
  );
}
